import React, { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, resolveAdminStatus } from '../config/firebase';
import { verifyTotp, notifyAdminLogin } from '../services/adminService';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const TOTP_KEY = 'dierend.admin.totpVerified';
const LOGIN_NOTIFIED_KEY = 'dierend.admin.loginNotified.uid';
const TOTP_TTL_MS = 60 * 60 * 1000; // 1 hour
const SERVER_TOTP_MAX_AGE_MS = 12 * 60 * 60 * 1000; // mirrors functions/adminAuth.js

// The server enforces 2FA through the `adminTotpAt` custom claim; a session
// cookie alone (e.g. from before the claim existed) is not enough.
const hasFreshTotpClaim = async (firebaseUser) => {
  try {
    const { claims } = await firebaseUser.getIdTokenResult();
    const at = claims?.adminTotpAt;
    return typeof at === 'number' && Date.now() - at <= SERVER_TOTP_MAX_AGE_MS;
  } catch {
    return false;
  }
};

const readTotpCookie = () => {
  try {
    const raw = sessionStorage.getItem(TOTP_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.uid || !parsed?.at) return null;
    if (Date.now() - parsed.at > TOTP_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
};

const writeTotpCookie = (uid) => {
  try {
    sessionStorage.setItem(TOTP_KEY, JSON.stringify({ uid, at: Date.now() }));
  } catch {
    /* ignore */
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [adminSource, setAdminSource] = useState(null);
  const [pending2FA, setPending2FA] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const finalizeLogin = async (firebaseUser, source) => {
    setUser({
      uid: firebaseUser.uid,
      email: firebaseUser.email,
      displayName: firebaseUser.displayName || firebaseUser.email?.split('@')[0],
    });
    setAdminSource(source);
    setError(null);

    // Fire login notification once per session per uid (best-effort).
    try {
      const lastNotifiedUid = sessionStorage.getItem(LOGIN_NOTIFIED_KEY);
      if (lastNotifiedUid !== firebaseUser.uid) {
        sessionStorage.setItem(LOGIN_NOTIFIED_KEY, firebaseUser.uid);
        notifyAdminLogin({ userAgent: navigator.userAgent });
      }
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        setUser(null);
        setAdminSource(null);
        setPending2FA(false);
        setPendingUser(null);
        setError(null);
        sessionStorage.removeItem(TOTP_KEY);
        sessionStorage.removeItem(LOGIN_NOTIFIED_KEY);
        setLoading(false);
        return;
      }

      try {
        const status = await resolveAdminStatus(firebaseUser);
        if (!status.isAdmin) {
          setUser(null);
          setAdminSource(null);
          setPending2FA(false);
          setPendingUser(null);
          setError('Unauthorized: This account does not have admin access');
          setLoading(false);
          return;
        }

        if (status.totpEnabled) {
          const cookie = readTotpCookie();
          if (cookie && cookie.uid === firebaseUser.uid && (await hasFreshTotpClaim(firebaseUser))) {
            // Already verified this session within the TTL window → finalize.
            await finalizeLogin(firebaseUser, status.source);
            setPending2FA(false);
            setPendingUser(null);
          } else {
            // Need 2FA challenge.
            setPending2FA(true);
            setPendingUser({
              uid: firebaseUser.uid,
              email: firebaseUser.email,
            });
            setUser(null);
            setAdminSource(status.source);
            setError(null);
          }
        } else {
          await finalizeLogin(firebaseUser, status.source);
          setPending2FA(false);
          setPendingUser(null);
        }
      } catch (err) {
        console.error('Admin status check failed:', err);
        setUser(null);
        setAdminSource(null);
        setPending2FA(false);
        setPendingUser(null);
        setError('Failed to verify admin access');
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit2FACode = async (code) => {
    if (!auth.currentUser) throw new Error('Not signed in');
    await verifyTotp(code);
    // Pick up the adminTotpAt claim the server just set; without a refreshed
    // token every admin callable would still be rejected.
    await auth.currentUser.getIdToken(true);
    writeTotpCookie(auth.currentUser.uid);
    await finalizeLogin(auth.currentUser, adminSource);
    setPending2FA(false);
    setPendingUser(null);
  };

  const cancel2FA = async () => {
    sessionStorage.removeItem(TOTP_KEY);
    setPending2FA(false);
    setPendingUser(null);
    try {
      await auth.signOut();
    } catch {
      /* ignore */
    }
  };

  /**
   * Force a fresh 2FA prompt before a sensitive action.
   * Wraps an async callback: if the user has 2FA enabled and their last
   * verification is older than 5 minutes, drop them back into the TOTP screen.
   */
  const requireFreshTotp = async (callback, maxAgeMs = 5 * 60 * 1000) => {
    if (!auth.currentUser) throw new Error('Not signed in');
    const cookie = readTotpCookie();
    if (cookie && cookie.uid === auth.currentUser.uid && Date.now() - cookie.at <= maxAgeMs) {
      return callback();
    }
    // Force fresh 2FA — clear cookie, set pending2FA, and abort.
    sessionStorage.removeItem(TOTP_KEY);
    setUser(null);
    setPending2FA(true);
    setPendingUser({ uid: auth.currentUser.uid, email: auth.currentUser.email });
    throw new Error('Re-verify your authenticator code to continue.');
  };

  const value = {
    user,
    adminSource,
    pending2FA,
    pendingUser,
    submit2FACode,
    cancel2FA,
    requireFreshTotp,
    loading,
    error,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
